import type { ComponentProps } from 'react';
import type { Th } from '@itandi/itandi-bb-ui';
import { colors } from '@itandi/itandi-bb-ui';
import type { DealConstructionSchedule } from '~/models/DealConstructionSchedule';
import type { DealUploadFile } from '~/models/DealUploadFile';
import type { VendorCompany } from '~/models/VendorCompany';
import type { Staff } from '~/models/Staff';
import type { InspectionCandidateDate } from '~/models/InspectionCandidateDate';
import type { CompanySetting } from '~/models/CompanySetting';
import type { DealCancel } from '~/models/DealCancel';
import type { DealVendorCompany } from '~/models/DealVendorCompany';
import { getOriginDealVendor } from '~/models/DealVendorCompany';
import type { DealStatusReason } from './DealStatusReason';
import type { VendorUser } from '~/models/VendorUser';
import type { DealEstimationContext } from '~/models/DealEstimate';
import type { DealBillingContext } from '~/models/DealBilling';
import type { StatusId } from '~/models/Status';
import { isOwnStatus } from '~/models/Status';
import type { Subtype } from '~/models/Subtype';
import type { CompanyStoreSetting } from './CompanyStoreSetting';
import type { DealRepair } from './DealRepair';
import type { PartnerDeal } from '~/models/sk/PartnerDeal';
import type { DealHistory } from '~/models/DealHistory';
import type { MessageSummaryUnread } from '~/models/MessageSummaryUnread';
import type { DealEstimationHistoryContext } from './DealEstimationHistory';
import type { PropertyBuildingOption } from './PropertyBuildingOption';
import type { PropertyRoomOption } from './PropertyRoomOption';

export type DealType = 'restoration' | 'repair' | 'other';
export const DEAL_TYPES = [
  'restoration',
  'repair',
  'other'
] as const satisfies ReadonlyArray<DealType>;
export type InspectionBy = 'vendor' | 'company' | 'none';
export const ESTIMATION_BY = { COMPANY: 'company', VENDOR: 'vendor' } as const;
type EstimationBy = (typeof ESTIMATION_BY)[keyof typeof ESTIMATION_BY];

export type Deal = {
  id: number;
  name: string;
  address: string;
  dealType: DealType;
  dealSubtype: Subtype | null;
  estimateAddressName: string;
  enableAutoLock: boolean;
  enableElevator: boolean;
  conductFieldSurvey: boolean;
  freeParking: string;
  note: string;
  preferredDate: string;
  roomNumber: string;
  placeKeyStoring: string;
  tenantName: string | null;
  tenantNameKana: string | null;
  tenantTel: string | null;
  tenantEmail: string | null;
  terminationDate: string;
  estimationBy: EstimationBy;
  inspectionBy: InspectionBy;
  inspectionDate: string | null;
  surveyDate: string | null;
  floorMap: string;
  leaseContractFile1: string | null;
  leaseContractFile1OriginalFilename: string | null;
  leaseContractFile2: string | null;
  leaseContractFile2OriginalFilename: string | null;
  leaseContractFile3: string | null;
  leaseContractFile3OriginalFilename: string | null;
  status: StatusId;
  createdAt: string;
  updatedAt: string;
  dealInspectionCandidateDates: Array<InspectionCandidateDate>;
  dealConstructionSchedules: DealConstructionSchedule[];
  dealRepair: DealRepair | null;
  dealCancel: DealCancel | null;
  dealMemorandumFiles: Array<DealUploadFile>;
  dealReceiptFiles: Array<DealUploadFile>;
  dealStatusReasons: DealStatusReason[];
  dealOtherFiles: Array<DealUploadFile>;
  dealCompanyPrivateFiles?: ReadonlyArray<DealUploadFile>;
  dealVendorCompanies: DealVendorCompany[];
  vendorCompany: VendorCompany;
  vendorCompanyUser: VendorUser;
  companySetting: CompanySetting;
  companyStoreSetting?: { name: string }; // 施工会社用のpartialでのみ店舗情報を返しているためオプショナル
  staff: Staff;
  propertyDealId: number;
  relatedDeals: RelatedDeal[];
  leasingId: number | null;
  propertyType: 'room' | 'building' | 'parking_space' | null; // KT経由で作成した案件は、parking_spaceになることがある(KTの契約は駐車場区画と紐づけることができるため)
  propertyId: number | null;
  buildingPropertyId: number | null;
  isRegisterAsBuilding: boolean;
  hasDraftEstimation: boolean;
  hasRequestEstimation: boolean;
  hasRequestedEstimation: boolean;
  latestEstimationRejected: boolean;
  completionReportImageRequired?: boolean;
  inspectionReportImageRequired?: boolean;
  dealVendorNoteId: number | null;
  messageSummaryUnread: MessageSummaryUnread | null;
  propertyBuildingOption?: PropertyBuildingOption;
  propertyRoomOption?: PropertyRoomOption | null;
};

export type DealWithCompanyAndVendor = {
  id: number;
  name: string;
  address: string;
  estimateAddressName: string;
  enableAutoLock: boolean;
  enableElevator: boolean;
  freeParking: string;
  note: string;
  preferredDate: string;
  roomNumber: string;
  placeKeyStoring: string;
  tenantName: string | null;
  tenantNameKana: string | null;
  tenantTel: string | null;
  tenantEmail: string | null;
  terminationDate: string;
  inspectionDate: string;
  floorMap: string;
  leaseContractFile1: string;
  leaseContractFile2: string;
  leaseContractFile3: string;
  status: StatusId;
  createdAt: string;
  updatedAt: string;
  originVendorCompany: VendorCompany | null;
  originVendorCompanyUser: VendorUser | null;
  companySetting: CompanySetting;
  staff: Staff | null;
};

/**
 * 案件一覧のDealModel(管理会社)
 */
export type DealIndexClient = {
  id: number;
  authId: number;
  propertyDealId: number;
  name: string;
  roomNumber: string | null;
  address: string;
  dealType: DealType;
  dealSubtype: Subtype | null;
  status: StatusId;
  tenantName: string | null;
  tenantNameKana: string | null;
  terminationDate: string | null;
  estimationBy: EstimationBy;
  inspectionBy: InspectionBy;
  inspectionDate: string | null;
  surveyDate: string | null;
  sendEstimateDate: string | null;
  orderDate: string | null;
  constructionStartDate: string | null;
  constructionEndDate: string | null;
  constructionReportedDate: string | null;
  compltedDate: string | null;
  leaseContractFile1: string;
  staff: Staff;
  user: VendorUser;
  companySetting: CompanySetting;
  companyStoreSetting: CompanyStoreSetting;
  dealVendorCompanies: DealVendorCompany[];
  completedDate: string;
  uncompletedTodoCount: number;
  unreadMessageCount: number;
  displayUnread: boolean;
  relatedDealCount: number;
  preferredDate: string | null;
  hasTemporaryDealCancel: boolean;
  hasRequestEstimation: boolean;
  hasDraftEstimationByCompany: boolean;
  hasRequestEstimationByCompany: boolean;
  updatedDate: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * 案件一覧のDealModel(施工会社)
 */
export type DealIndexVendor = {
  id: number;
  name: string;
  roomNumber: string | null;
  address: string;
  dealType: DealType;
  dealSubtype: Subtype | null;
  status: StatusId;
  tenantName: string | null;
  tenantNameKana: string | null;
  terminationDate: string | null;
  inspectionBy: InspectionBy;
  inspectionDate: string | null;
  surveyDate: string | null;
  sendEstimateDate: string | null;
  orderDate: string | null;
  constructionStartDate: string | null;
  constructionEndDate: string | null;
  constructionReportedDate: string | null;
  compltedDate: string | null;
  preferredDate: string | null;
  leaseContractFile1: string | null;
  staff: Staff;
  user: VendorUser;
  completionReportImageRequired: boolean;
  originVendorCompany: VendorCompany;
  originVendorUserName: string | null;
  subVendorCompany: VendorCompany | null;
  subVendorUserName: string | null;
  companySetting: CompanySetting;
  maintenanceCompanyName: string;
  maintenanceCompanyStoreName: string;
  completedDate: string;
  dealHistories: DealHistory[];
  unreadMessageCount: number;
  displayUnread: boolean;
  hasTemporaryDealCancel: boolean;
  hasRequestEstimation: boolean;
  hasDraftEstimation: boolean;
  hasRequestEstimationByCompany: boolean;
  updatedDate: string;
  createdAt: string;
  updatedAt: string;
};

export type DealWithPrices = {
  id: number;
  dealType: DealType;
  dealSubtype: Subtype | null;
  status: StatusId;
  originDealVendorCompany: DealVendorCompany | null; // TODO: Dealに合わせて、dealVendorCompaniesから特定するようにしたい。NSKDEV-2234
  dealVendorCompanies: DealVendorCompany[];
  dealEstimationHistoryContext: DealEstimationHistoryContext;
  dealEstimationContext: DealEstimationContext;
  dealOwnerBillingContext: DealBillingContext;
  dealRenteeBillingContext: DealBillingContext;
  skPartnerDeal: PartnerDeal | null;
  requestEstimationHeader: boolean;
  estimationBy: EstimationBy;
  inspectionBy: InspectionBy;
  inspectionDate: string | null;
  inspectionReportImageRequired?: boolean;
  completionReportImageRequired?: boolean;
  companySetting: Pick<CompanySetting, 'fromMarket' | 'antisocialCheckId'>;
};

/**
 * 関連する案件
 */
export type RelatedDeal = {
  id: number;
  name: string;
  roomNumber: string;
  createdAt: string;
  updatedAt: string;
};

export type SearchApplicationType =
  | 'cancel'
  | 'draftEstimation' // 施工会社が編集中の見積り
  | 'draftEstimationByCompany'
  | 'requestEstimation' // 施工会社が申請した見積り
  | 'requestEstimationByCompany';

export type DealSearchCondition = {
  name?: string;
  roomNumber?: string;
  address?: string;
  tenantName?: string;
  companyMasterId?: number;
  authId?: number;
  companyVendorId?: number;
  statusId?: number[];
  dealType?: DealType;
  dealSubtypeId?: number | null;
  dealTypes?: DealType[];
  dealSubtypeIds?: number[];
  applicationTypes?: SearchApplicationType[];
  staffId?: number;
  userId?: number;
  partnerVendorId?: number;
  constructionMinStartDate?: string;
  constructionMaxStartDate?: string;
  constructionMinEndDate?: string;
  constructionMaxEndDate?: string;
  inspectionMinDate?: string;
  inspectionMaxDate?: string;
  surveyMinDate?: string;
  surveyMaxDate?: string;
  sendEstimateMinDate?: string;
  sendEstimateMaxDate?: string;
  orderMinDate?: string;
  orderMaxDate?: string;
  constructionReportedMinDate?: string;
  constructionReportedMaxDate?: string;
  completedMinDate?: string;
  completedMaxDate?: string;
  registrationMinDate?: string;
  registrationMaxDate?: string;
  updatedMinDate?: string;
  updatedMaxDate?: string;
  prioritySort?: boolean;
  page?: number;
  sortKey?: DealSortKey;
  sortDirection?: ComponentProps<typeof Th>['sort'];
};

export type DealSortKey =
  | 'registration_date'
  | 'status_id'
  | 'termination_date'
  | 'inspection_date'
  | 'survey_date'
  | 'send_estimate_date'
  | 'order_date'
  | 'construction_start_date'
  | 'construction_end_date'
  | 'construction_reported_date'
  | 'completed_date'
  | 'preferred_date'
  | 'updated_date';

export const isNumberCondition = (key: string): boolean =>
  [
    'companyMasterId',
    'authId',
    'companyVendorId',
    'staffId',
    'userId',
    'partnerVendorId',
    'page'
  ].includes(key);

const isDetailCondition = (key: string): boolean =>
  [
    'dealTypes',
    'statusId',
    'applicationTypes',
    'name',
    'address',
    'tenantName',
    'companyMasterId',
    'companyVendorId',
    'partnerVendorId',
    'authId',
    'staffId',
    'userId',
    'constructionMinStartDate',
    'constructionMaxStartDate',
    'constructionMinEndDate',
    'constructionMaxEndDate',
    'inspectionMinDate',
    'inspectionMaxDate',
    'registrationMinDate',
    'registrationMaxDate',
    'updatedMinDate',
    'updatedMaxDate',
    'prioritySort'
  ].includes(key);

export const searchDetailBadge = (
  dealSearchCondition: DealSearchCondition
): number => {
  let ret = 0;
  Object.entries(dealSearchCondition).forEach(([key, value]) => {
    if (isDetailCondition(key)) {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          ret += 1;
        }
      } else if (value) {
        ret += 1;
      }
    }
  });
  return ret;
};

export const isWithVendorDeal = (deal: Deal): boolean => {
  const originDealVendor = getOriginDealVendor(deal);
  if (originDealVendor === null) {
    // 施工会社未選択は一旦施工会社も呼ぶ前提として扱う
    return true;
  }
  return originDealVendor.vendorCompany !== null;
};

export const matchDealType = (
  deal: Deal | DealIndexClient | DealIndexVendor,
  dealType: 'restoration' | 'repair' | 'other'
): boolean => deal.dealType === dealType;

export const INSPECTION_BY_LABEL = {
  vendor: '施工会社',
  company: '管理会社',
  none: 'なし'
} as const satisfies {
  [key in InspectionBy]: string;
};

export const ESTIMATION_BY_LABEL = {
  vendor: '施工会社',
  company: '管理会社'
} as const satisfies {
  [key in EstimationBy]: string;
};

export const DEAL_TYPE_LABEL = {
  restoration: '原状回復',
  repair: '修繕',
  other: 'その他'
} as const;
// typescript4.9, next13以降からしかsatisfiesが使えない
// } as const satisfies {
//   [key in DealType]: string;
// }

// 立会報告画像が必須かどうかを判定する
export const checkInspectionImageRequired = (
  deal: Deal | DealWithPrices
): boolean =>
  deal.inspectionBy === 'vendor' && !!deal.inspectionReportImageRequired;

// 案件少佐画面の見積りの状態を返す関数。それぞれの状態はすべてユニークなので順番は関係なく動作する
export const estimationHeaderStatus = (
  deal: Deal
): { label: string; color: string; description: string } => {
  // 非共有案件はheader利用しない仕様になったが、過去データが残っているため分岐させる
  // https://itandi-dev.atlassian.net/browse/NSKDEV-1622で削除
  if (isOwnStatus(deal.status))
    return { label: '', color: '', description: '' };
  if (deal.hasDraftEstimation)
    return {
      label: '編集中',
      color: colors['expressive-yellow-dark'],
      description:
        '見積編集中のため、見積り内容は確定しておりません。見積りの修正が完了次第、見積りを申請するボタンを押してください。'
    };
  if (deal.hasRequestEstimation)
    return {
      label: '申請中',
      color: colors['expressive-blue'],
      description:
        '見積申請中のため、見積り内容は確定しておりません。見積りが反映されるまでお待ち下さい。'
    };
  if (deal.hasRequestedEstimation)
    return {
      label: '申請あり',
      color: colors['expressive-aqua-dark'],
      description:
        '見積りの申請がございます。反映・承認を行い、見積内容を確定させてください。'
    };
  if (deal.latestEstimationRejected)
    return {
      label: '',
      color: '',
      description:
        '申請した見積りが棄却されております。修正を行いたい場合には、再度見積りを編集し申請をおこなってください。'
    };
  return { label: '', color: '', description: '' };
};

// マケプレ案件かつ反社チェックID未登録の場合は true
export const isInvalidAntisocialCheck = (
  deal: Deal | DealWithPrices
): boolean =>
  deal.companySetting.fromMarket &&
  deal.companySetting.antisocialCheckId === null;
